import React, { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';

const DEFAULT_GOOGLE_LINK = 'https://maps.app.goo.gl/BAnEKABagyxsfFvQ9';

export default function GoogleReviews({ googleLink = DEFAULT_GOOGLE_LINK }) {
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const res = await fetch('/api/google-reviews');
        if (!mounted) return;
        if (!res.ok) {
          setError('Não foi possível obter avaliações (sem chave/config).');
          setReviews([]);
        } else {
          const json = await res.json();
          setReviews(json.reviews || []);
        }
      } catch (err) {
        setError(err.message);
        setReviews([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return <div className="p-4 bg-gray-50 rounded-xl">Carregando avaliações...</div>;

  if (reviews && reviews.length > 0) {
    const avg = (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1);
    return <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 bg-gray-50 p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex flex-col gap-1">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} size={18} className={`fill-[#c99b36] text-[#c99b36] ${i <= Math.round(avg) ? '' : 'opacity-40'}`} />)}
          </div>
          <div className="flex gap-2 items-baseline">
            <span className="font-bold text-xl text-gray-900">{avg}</span>
            <span className="text-sm text-gray-500">de {reviews.length}.0</span>
          </div>
        </div>

        <div className="hidden sm:block w-px h-12 bg-gray-200"></div>

        <div className="flex-1 grid gap-3">
          {reviews.slice(0, 3).map((r, idx) => <div key={idx} className="bg-white p-3 rounded-lg border border-gray-100">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-sm text-gray-700">{r.author_name || 'Usuário'}</div>
                <div className="text-sm text-gray-500">{r.relative_time_description || ''}</div>
              </div>
              <div className="text-sm text-gray-700 mt-2">{r.text}</div>
            </div>)}
          <a href={googleLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#0a4b3c] font-semibold">Ver todas as avaliações <ExternalLink size={12} /></a>
        </div>
      </div>;
  }

  // Fallback: render a call-to-action linking to Google
  return <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 bg-gray-50 p-4 rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex flex-col gap-1">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(star => <Star key={star} size={18} className="fill-[#c99b36] text-[#c99b36]" />)}
        </div>
        <div className="flex gap-2 items-baseline">
          <span className="font-bold text-xl text-gray-900">5.0</span>
          <span className="text-sm text-gray-500">de 5.0</span>
        </div>
      </div>

      <div className="hidden sm:block w-px h-12 bg-gray-200"></div>

      <a href={googleLink} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 bg-white hover:bg-gray-50 px-5 py-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
        <span className="text-sm font-semibold text-gray-700 group-hover:text-[#0a4b3c] transition-colors">Ver avaliações</span>
        <ExternalLink size={14} className="text-gray-500 group-hover:text-[#0a4b3c]" />
      </a>
    </div>;
}
